import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader } from 'lucide-react';

/**
 * Reusable Form Dialog wrapper component
 * Handles open/close state, form submission, and loading states
 * 
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onOpenChange - Handler for open state changes
 * @param {string} props.title - Dialog title
 * @param {ReactNode} props.children - Form fields (rendered inside form)
 * @param {Function} props.onSubmit - Form submit handler
 * @param {boolean} props.loading - Loading state for submit button
 * @param {string} props.submitLabel - Submit button text
 * @param {boolean} props.showCancel - Show cancel button
 * @param {string} props.size - Dialog size: 'sm', 'md', 'lg', 'xl'
 */
export const FormDialog = ({
  open,
  onOpenChange,
  title,
  children,
  onSubmit,
  loading = false,
  submitLabel = 'Submit',
  showCancel = true,
  size = 'md',
  testId = 'form-dialog'
}) => {
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl'
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (onSubmit) {
      await onSubmit(e);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className={`${sizeClasses[size]} max-h-[85vh] overflow-y-auto`}
        data-testid={testId}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {children}
          
          <div className="flex gap-2 justify-end pt-4 border-t">
            {showCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              className="bg-gradient-to-r from-blue-600 to-indigo-600"
              disabled={loading}
              data-testid={`${testId}-submit`}
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                submitLabel
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Form field wrapper for consistent styling
 */
export const FormField = ({ label, error, required, children, htmlFor }) => {
  return (
    <div className="space-y-2">
      {label && (
        <label 
          htmlFor={htmlFor}
          className="text-sm font-medium text-gray-700"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      {children}
      {error && (
        <p className="text-xs text-red-600">{error}</p>
      )}
    </div>
  );
};